/**
Project: Voice Panel (c)
Title: Presence 
Description: Component to select the voice regions. 
Copyrights: This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this source code package.
*/
import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import {
  Flex,
  Text,
  Button,
  RadioGroup,
  Checkbox,
} from "@fluentui/react-northstar";
import { Grid, Row, Col } from "react-flexbox-grid";
import PageUtil from "../../../../core/helpers/page-util";
import AlertErrorMessage from "../../common/helpers/alert-error-message";
import IntlUtil from "../../../../core/helpers/intl-util";
import PageLoader from "../../common/helpers/page-loader";
import { manageError } from "../../../../core/actions/common-actions";
import { AppConfigProps } from "../../../../core/settings/app-config";
import {
  setVoiceSetupProgressPage,
  getVoiceRegions,
  setVoiceSetupProgressRegions,
} from "../actions/voice-setup-actions";
import { VoiceAdminURLProps } from "../../common/settings/voice-admin-urls";
import { VoiceAdminConstants } from "../../common/settings/voice-admin-constants";
import PageExitGuard from "../../common/helpers/page-exit-guard";
import _ from "lodash";

class Presence extends Component {
  _isMounted = false;
  _axiosSource = axios.CancelToken.source();
  _cancelToken = { cancelToken: this._axiosSource.token };
  _intl_ns_common = "common";
  _intl_ns_vpa_voicesetup = "vpa_voicesetup";

  constructor(props) {
    super(props);
    this.state = {
      isPageDataFetched: false,
      isFormDataFilled: false,
      voiceRegions: null,
      presenceType: null,
      selectedVoiceRegions: [],
      checked: false,
      isError: false,
      errorMessage: null,
      atLeastOneRegionSelected: false,
    };
  }

  setStateAsync = (state) => {
    if (this._isMounted) {
      return new Promise((resolve) => {
        this.setState(state, resolve);
      });
    }
  };

  async componentDidMount() {
    this._isMounted = true;
    PageUtil.scrollToTop();
    await this.checkAccountNumber();
    await this.loadPageData();
  }
  componentWillUnmount() {
    this._isMounted = false;
    this._axiosSource.cancel(
      IntlUtil.getText(
        this._intl_ns_common,
        "notification.warning.requestCancelled"
      )
    );
  }
  checkAccountNumber = async () => {
    if (
      this.props.voiceSetupProgressAccountNumber === null ||
      this.props.voiceSetupProgressAccountNumber.trim() === ""
    ) {
      await this.props.setVoiceSetupProgressPage(
        VoiceAdminConstants.VOICE_SETUP.PROGRESS.WELCOME
      );
    }
  };

  loadPageData = async () => {
    try {
      await this.setStateAsync({ isPageDataFetched: false });
      await this.getVoiceRegions();
      await this.setStateAsync({ isPageDataFetched: true });
    } catch (err) {
      await this.setState({ isError: true });
      await manageError(err, this.props.history);
    }
  };

  getVoiceRegions = async () => {
    await getVoiceRegions(this._cancelToken)
      .then(async (res) => {
        if (
          res &&
          res.status === AppConfigProps.httpStatusCode.ok &&
          res.data &&
          res.data.records &&
          res.data.records?.length > 0
        ) {
          var records = await res.data.records.map((region) => {
            return {
              ...region,
              isChecked: region?.configStatus === 1 ? true : false,
              isDisabled: region?.configStatus === 1 ? true : false,
            };
          });
          records = _.orderBy(records, "displayOrder", ["asc"]);
          let atLeastOneRegionSelected = records.some((rec) => rec.isDisabled);
          let allRegionsSelected = records.every((rec) => rec.isDisabled);
          if (
            allRegionsSelected &&
            this.props.subscriberAccount &&
            this.props.subscriberAccount.voiceRerunSetupPrivilege &&
            this.props.subscriberAccount.voiceRerunSetupPrivilege === true
          ) {
            let regionNames = records.map((rec) => rec.regionName);
            await this.setStateAsync({
              selectedVoiceRegions: regionNames,
            });
            await this.checkFormData();
          }
          await this.setStateAsync({
            atLeastOneRegionSelected,
            voiceRegions: records,
          });
        } else {
          await this.setState({ isError: true });
          await manageError(res, this.props.history);
        }
      })
      .catch(async (err) => {
        await this.setState({ isError: true });
        await manageError(err, this.props.history);
      });
  };

  checkFormData = async () => {
    if (
      this.state.selectedVoiceRegions &&
      this.state.selectedVoiceRegions.length > 0
    ) {
      await this.setStateAsync({ isFormDataFilled: true });
    } else {
      await this.setStateAsync({ isFormDataFilled: false });
    }
  };

  handlePresenceTypeChange = async (e, option) => {
    await this.state.voiceRegions.map((region) => {
      return (region.isChecked = false);
    });
    await this.setStateAsync({ presenceType: option.value });
    await this.setStateAsync({ selectedVoiceRegions: [] });
    await this.checkFormData();
  };

  renderGlobalOptions() {
    return (
      <Row>
        <Col xs={12} sm={12} md={12} lg={6}>
          <Flex column>
            <Row>
              <Col xs={12} className="m-b-10">
                <Text className="page-text">
                  {IntlUtil.getText(
                    this._intl_ns_vpa_voicesetup,
                    "content.presence.selectGlobalInfo"
                  )}
                </Text>
                {!this.state.atLeastOneRegionSelected ? (
                  <Text className="page-text m-l-5">
                    {IntlUtil.getText(
                      this._intl_ns_vpa_voicesetup,
                      "content.presence.selectAtLeastOneRegion"
                    )}
                  </Text>
                ) : null}
              </Col>
            </Row>
            {this.state.voiceRegions.map((region, index) => {
              return (
                <Row key={`global-region-${index}`}>
                  <Col xs={12} className="m-b-5">
                    <Checkbox
                      role="checkbox"
                      label={region.regionDescription}
                      disabled={region.isDisabled}
                      checked={region.isChecked}
                      onChange={(e, option) =>
                        this.handleGlobalOptionChange(e, option, region)
                      }
                      className="page-checkbox"
                    />
                  </Col>
                </Row>
              );
            })}
          </Flex>
        </Col>
      </Row>
    );
  }

  handleGlobalOptionChange = async (event, option, region) => {
    let voiceRegions = this.state.voiceRegions;
    let globalSelectedItems = this.state.selectedVoiceRegions;
    if (globalSelectedItems) {
      voiceRegions = voiceRegions.map((voiceRegions) => {
        if (voiceRegions.regionName === region?.regionName) {
          if (
            option.checked === true &&
            globalSelectedItems.includes(voiceRegions.regionName) === false
          ) {
            globalSelectedItems.push(voiceRegions.regionName);
            voiceRegions.isChecked = true;
          }
          if (option.checked === false) {
            if (
              globalSelectedItems.includes(voiceRegions.regionName) === true
            ) {
              voiceRegions.isChecked = false;
              let newItems = globalSelectedItems.filter((opt) => {
                return opt !== voiceRegions.regionName;
              });
              globalSelectedItems = [...newItems];
            }
          }
        }
        return { ...voiceRegions };
      });
      if (voiceRegions?.length > 0)
        await this.setStateAsync({
          voiceRegions: [...voiceRegions],
          errorMessage: null,
        });
      await this.setStateAsync({ selectedVoiceRegions: globalSelectedItems });
      await this.checkFormData();
    }
  };

  handlePresenceSubmit = async () => {
    if (
      this.state.selectedVoiceRegions &&
      (this.state.selectedVoiceRegions.length > 1 ||
        (this.state.selectedVoiceRegions.length >= 1 &&
          this.state.voiceRegions.some((reg) => reg.isDisabled === true)))
    ) {
      this.props.setVoiceSetupProgressRegions(this.state.selectedVoiceRegions);
      await this.loadNextPage();
    } else {
      await this.setStateAsync({
        errorMessage: IntlUtil.getText(
          this._intl_ns_vpa_voicesetup,
          "notification.error.regionSelectFailed"
        ),
      });
    }
  };

  loadNextPage = async () => {
    await this.props.setVoiceSetupProgressPage(
      VoiceAdminConstants.VOICE_SETUP.PROGRESS.SETUP
    );
  };

  loadHomePage = async () => {
    await this.props.setVoiceSetupProgressPage(
      VoiceAdminConstants.VOICE_SETUP.PROGRESS.WELCOME
    );
    this.props.history.replace(VoiceAdminURLProps.home);
  };

  render() {
    return (
      <Grid fluid className="page-grid m-t-15">
        <Row>
          <Col xs={12} className="m-b-20">
            <Text className="page-heading4">
              {IntlUtil.getText(
                this._intl_ns_vpa_voicesetup,
                "content.presence.selectRegion"
              )}
            </Text>
          </Col>
        </Row>
        <PageLoader
          status={this.state.isPageDataFetched}
          size="small"
          labelPosition="end"
          label={IntlUtil.getText(
            this._intl_ns_common,
            "content.loadingInprogress"
          )}
          type="inline"
        >
          <>
            {this.state.voiceRegions && this.state.voiceRegions.length > 0 ? (
              <>
                {this.renderGlobalOptions()}
                {this.state.errorMessage !== null ? (
                  <Row>
                    <Col xs={12} className="m-t-20 m-b-15">
                      <Text className="page-text text-error">
                        {this.state.errorMessage}
                      </Text>
                    </Col>
                  </Row>
                ) : null}
                <Row>
                  <Col xs={12} className="m-t-20 m-b-20">
                    <Button
                      primary
                      content={IntlUtil.getText(
                        this._intl_ns_vpa_voicesetup,
                        "content.continueSetup"
                      )}
                      disabled={!this.state.isFormDataFilled}
                      onClick={this.handlePresenceSubmit}
                      className="vpa-voice-setup-button"
                    />
                  </Col>
                </Row>
              </>
            ) : (
              <>
                <Row>
                  <Col xs={12} className="m-b-20 m-t-20">
                    <AlertErrorMessage
                      content={
                        <>
                          {IntlUtil.getText(
                            this._intl_ns_vpa_voicesetup,
                            "notification.error.retrieveRegionsFailed"
                          )}
                        </>
                      }
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} className="m-t-20 m-b-20">
                    <Button
                      primary
                      content={IntlUtil.getText(
                        this._intl_ns_vpa_voicesetup,
                        "content.home"
                      )}
                      onClick={this.loadHomePage}
                      className="vpa-voice-setup-button"
                    />
                  </Col>
                </Row>
              </>
            )}
          </>
        </PageLoader>
        <PageExitGuard
          when={this.state?.isError === false}
          navigate={(path) => this.props.history.push(path)}
          shouldBlockNavigation={(location) => {
            if (location.pathname) {
              return true;
            }

            return false;
          }}
        />
      </Grid>
    );
  }
}

const mapStateToProps = (state) => ({
  voiceSetupProgressPage: state.voiceSetupStore.voiceSetupProgressPage,
  voiceSetupProgressAccountNumber:
    state.voiceSetupStore.voiceSetupProgressAccountNumber,
  subscriberAccount: state.subscriberStore.subscriberAccount,
});

const mapActionToProps = {
  setVoiceSetupProgressPage,
  setVoiceSetupProgressRegions,
};

export default connect(mapStateToProps, mapActionToProps)(Presence);
